<script setup>
import { validateGiftCode } from '@/api/gifts';

definePage({ meta: { layout: 'purchase' } });

const router = useRouter();
const route = useRoute();

const code = computed(() => route.query.code);
const type = computed(() => route.query.type || 'inbox');

const giftInfo = ref(null);
const isLoading = ref(true);

// 만료일 포맷
const formattedExpiredAt = computed(() => {
  if (!giftInfo.value?.expiredAt) return '';
  const date = new Date(giftInfo.value.expiredAt);
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
});

// 메시지
const displayMessage = computed(() => {
  if (!giftInfo.value) return '';
  return (
    giftInfo.value.message ||
    `${giftInfo.value.senderName}님께서 따뜻한 응원의 마음으로 보내신 선물이에요.\n편한 시간에 확인해보세요.`
  );
});

// 상태별 배지 텍스트
const badgeText = computed(() => {
  if (!giftInfo.value) return '';
  if (type.value === 'outbox') {
    return '🎁 선물을 보냈어요!';
  }
  return '🎁 선물이 왔어요!';
});

// 상태별 만료일 레이블
const expiredLabel = computed(() => {
  if (!giftInfo.value) return '만료일';
  switch (giftInfo.value.status) {
    case 'ACCEPTED':
      return '사용 만료일';
    case 'EXPIRED':
      return '기간 만료일';
    case 'PENDING':
    default:
      return '수락 만료일';
  }
});

// 상태별 만료 안내 문구
const expiredNotice = computed(() => {
  if (!giftInfo.value) return '';
  switch (giftInfo.value.status) {
    case 'ACCEPTED':
      return '사용 만료일이 지나면 선물이 자동으로 만료됩니다.';
    case 'EXPIRED':
      return '기간이 만료된 선물입니다.';
    case 'PENDING':
    default:
      return '수락 만료일이 지나면 선물이 자동으로 만료됩니다.';
  }
});

/** 선물 정보 조회 */
const fetchGiftInfo = async () => {
  if (!code.value) {
    router.push('/purchase/gift-box');
    return;
  }

  isLoading.value = true;
  try {
    const result = await validateGiftCode(code.value);
    giftInfo.value = result;
  } catch (e) {
    console.error('선물 정보 조회 실패:', e);
    router.push('/purchase/gift-box');
  } finally {
    isLoading.value = false;
  }
};

/** 뒤로가기 */
const handleBack = () => {
  router.push({ path: '/purchase/gift-box', query: { tab: type.value } });
};

onMounted(() => {
  fetchGiftInfo();
});
</script>

<template>
  <div class="gift-view-wrap">
    <!-- app -->
    <div class="com-app-tit-box">
      <div class="btn-left">
        <button class="btn-back" @click="handleBack">
          <IconHeaderBack />
        </button>
      </div>
      <div class="tit">선물 상세</div>
    </div>

    <!-- 로딩 중 -->
    <div v-if="isLoading" class="gift-loading-box">
      <div class="loading-spinner"></div>
    </div>

    <!-- 선물 정보 -->
    <div v-else-if="giftInfo" class="gift-view" :class="`gift-view--${type}`">
      <div class="gift-view-content">
        <!-- 1. 선물(분석권) 정보 -->
        <section class="gift-ticket">
          <div class="gift-ticket__header">
            <span class="gift-ticket__badge">{{ badgeText }}</span>
            <h1 class="gift-ticket__title">{{ giftInfo.productName }}</h1>
          </div>
          <div class="gift-ticket__body">
            <p class="gift-ticket__description">
              지금의 나에게 어떤 기운이 힘이 되는지,<br />가볍게 확인해 볼 수 있는 작은 안내서!
            </p>
          </div>
        </section>

        <!-- 3. 보낸분 정보 및 메시지 -->
        <section class="gift-section gift-section--sender">
          <div class="gift-bubble">
            <div class="gift-bubble__text">
              '{{ giftInfo.senderName || '익명' }}' 님께서 보내신 선물입니다
            </div>
            <div class="gift-bubble__tail"></div>
          </div>

          <div class="gift-card">
            <div class="gift-card__message">
              "
              <br />
              {{ displayMessage }}
              <br />
              "
            </div>
            <div class="gift-card__balloon"></div>
          </div>
        </section>

        <!-- 2. 유효기간 정보 -->
        <section class="gift-section gift-validity">
          <div class="gift-validity__notice">{{ type === 'inbox' ? '❗' : '❕' }}확인해주세요</div>
          <ul>
            <li>
              <span>
                {{ expiredLabel }}: <span class="highlight">{{ formattedExpiredAt }}</span>
              </span>
            </li>
            <li>{{ expiredNotice }}</li>
          </ul>
        </section>
      </div>

      <!-- 목록으로 버튼 (웹) -->
      <section class="btn-com gap-xl center">
        <button class="btn-line w-large bg-w" @click="handleBack">목록으로</button>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/components/purchase/gift-valid.scss';

.gift-view-wrap {
  background-color: #fff;
  min-height: 100vh;
}

.gift-loading-box {
  width: 100%;
  padding: 100px 0;
  display: flex;
  justify-content: center;
  align-items: center;

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e5e5;
    border-top-color: #eb4c10;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.gift-view {
  word-break: keep-all;
  max-width: 420px;
  margin: 0 auto;

  .gift-view-content {
    padding: 0;
  }

  .gift-ticket {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .gift-ticket__header,
  .gift-ticket__body {
    padding: 0;
  }

  .gift-ticket__title {
    text-align: center;
    font-family: Pretendard;
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
  }

  .gift-section--sender {
    padding: 24px 32px 32px;
  }

  // 받은 선물함
  &--inbox {
    .gift-ticket,
    .gift-validity {
      background: #ffd46c;
      color: var(--Gray-800, #333);
    }

    .gift-ticket__badge {
      background: #fff;
      color: var(--Gray-800, #333);
    }

    .gift-ticket__title,
    .gift-ticket__description,
    .gift-validity__notice,
    .gift-validity li {
      color: var(--Gray-800, #333);
    }

    .gift-validity li .highlight {
      color: var(--Orange-500-Error, #ff453a);
    }
  }

  // 보낸 선물함
  &--outbox {
    .gift-ticket,
    .gift-validity {
      background: #4a5365;
      color: var(--Gray-800, #333);
    }

    .gift-ticket__badge {
      background: #2c323d;
    }

    .gift-ticket__title {
      color: #ffd557;
    }
  }

  .btn-com {
    padding: 40px 0;
    display: flex;
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .gift-view-wrap {
    height: 100vh;

    .com-app-tit-box {
      position: fixed;
    }
  }

  .gift-view {
    margin: 52px 0 0;
    max-width: 100%;

    .gift-section--sender {
      padding: 24px;
    }

    .btn-com {
      display: none;
    }
  }
}
</style>
